import React, { ReactNode, useState, useRef, useEffect, MouseEvent } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: ReactNode;
  children: React.ReactElement;
}

function combineHandlers<T extends MouseEvent>(...handlers: Array<((e: T) => void) | undefined>): (e: T) => void {
  return (e: T) => {
    for (const handler of handlers) {
      if (typeof handler === 'function') {
        handler(e);
      }
    }
  };
}

export function Tooltip({ content, children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => setVisible(true);
  const hideTooltip = () => setVisible(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (tooltipRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      tooltipRef.current.style.left = `${triggerRect.left + triggerRect.width / 2}px`;
      tooltipRef.current.style.top = `${triggerRect.top - 40}px`;
    }
  };

  const enhancedChildren = React.cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: combineHandlers(showTooltip, children.props?.onMouseEnter),
    onMouseLeave: combineHandlers(hideTooltip, children.props?.onMouseLeave),
    onMouseMove: combineHandlers(handleMouseMove, children.props?.onMouseMove),
  });

  const tooltip = visible && createPortal(
    <div
      ref={tooltipRef}
      className="absolute z-50 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg pointer-events-none"
      style={{ transform: 'translateX(-50%) translateY(-100%)', marginTop: '-8px' }}
    >
      {content}
    </div>,
    document.body
  );

  return (
    <>
      <div onMouseEnter={showTooltip} onMouseLeave={hideTooltip} onMouseMove={handleMouseMove}>
        {enhancedChildren}
      </div>
      {tooltip}
    </>
  );
}